import { useState } from 'react';
import { errorMessage } from '../api/client';
import { eventsApi, newEventId } from '../api/endpoints';
import type { Employee, TimelineEvent } from '../api/types';
import {
  INTERVENTION_CATEGORIES,
  NATURE_LABELS,
  eventTypeLabel,
  interventionCategoryLabel,
  natureOf,
  reasonRequiredFor,
  type InterventionCategory,
  type ManualEventType,
} from '../domain/events';
import {
  WORKSTATION_STATES,
  WORKSTATION_STATE_LABELS,
  type WorkstationState,
} from '../domain/workstations';
import { formatDateTime, fromDateTimeInput, fullName, nowForInput } from '../lib/format';
import { Field } from './Field';
import { Modal } from './Modal';
import { Notice } from './Notice';

type Fields = {
  salarieId: string;
  etat: WorkstationState;
  categorie: InterventionCategory;
  description: string;
  dureeMinutes: string;
  destination: string;
};

function emptyFields(currentState: WorkstationState | null): Fields {
  return {
    salarieId: '',
    etat: currentState ?? 'enService',
    categorie: 'materiel',
    description: '',
    dureeMinutes: '',
    destination: '',
  };
}

function buildPayload(type: ManualEventType, fields: Fields): Record<string, unknown> {
  switch (type) {
    case 'changementProprietaire':
      // valeur nulle : le poste revient au parc sans détenteur
      return { salarieId: fields.salarieId === '' ? null : fields.salarieId };
    case 'changementEtat':
      return { etat: fields.etat };
    case 'intervention': {
      const payload: Record<string, unknown> = {
        categorie: fields.categorie,
        description: fields.description,
      };
      if (fields.dureeMinutes !== '') {
        payload.dureeMinutes = Number.parseInt(fields.dureeMinutes, 10);
      }
      return payload;
    }
    case 'miseEnService':
      return {};
    case 'miseAuRebut': {
      const payload: Record<string, unknown> = {};
      if (fields.destination !== '') payload.destination = fields.destination;
      return payload;
    }
  }
}

/**
 * Date du dernier événement effectif de même nature, s'il en existe un : c'est
 * elle qu'un événement antidaté ne dépassera pas.
 */
function lastOfSameNature(
  events: TimelineEvent[],
  type: ManualEventType,
): TimelineEvent | null {
  const nature = natureOf(type);
  if (nature === null) return null;
  return events
    .filter((event) => !event.cancelled && natureOf(event.type) === nature)
    .reduce<TimelineEvent | null>(
      (latest, event) =>
        latest === null || event.occurredAt > latest.occurredAt ? event : latest,
      null,
    );
}

export function EventModal({
  type,
  workstationId,
  currentState,
  employees,
  events,
  onClose,
  onSaved,
}: {
  type: ManualEventType;
  workstationId: string;
  currentState: WorkstationState | null;
  employees: Employee[];
  /** Frise du poste, pour détecter une saisie antidatée. */
  events: TimelineEvent[];
  onClose: () => void;
  onSaved: (event: TimelineEvent) => void;
}) {
  const [occurredAt, setOccurredAt] = useState(nowForInput);
  const [reason, setReason] = useState('');
  const [fields, setFields] = useState<Fields>(() => emptyFields(currentState));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reasonRequired = reasonRequiredFor(type);
  const nature = natureOf(type);
  const previous = lastOfSameNature(events, type);
  const antidated =
    previous !== null && fromDateTimeInput(occurredAt) < previous.occurredAt;

  const patch = (change: Partial<Fields>): void =>
    setFields((current) => ({ ...current, ...change }));

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    eventsApi
      .append({
        id: newEventId(),
        workstationId,
        type,
        occurredAt: fromDateTimeInput(occurredAt),
        reason,
        payload: buildPayload(type, fields),
      })
      .then(({ event: saved }) => onSaved(saved))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={eventTypeLabel(type)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-evenement"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer au journal'}
          </button>
        </>
      }
    >
      <form id="formulaire-evenement" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}

        {type === 'changementEtat' && !antidated && (
          <Notice tone="info">
            L'état du poste est calculé depuis le journal. Cet événement devient son
            état courant ; l'annuler rendra sa place à l'état précédent.
          </Notice>
        )}

        {antidated && nature !== null && previous !== null && (
          <Notice tone="alerte" title="Événement antidaté">
            Un événement de même nature existe déjà au{' '}
            {formatDateTime(previous.occurredAt)}, soit après la date saisie. Celui-ci
            sera bien enregistré dans l'historique du poste, mais il ne modifiera pas{' '}
            {NATURE_LABELS[nature]} : c'est l'événement le plus récent qui fait foi.
          </Notice>
        )}

        <div className="grille-champs">
          <Field
            label="Date de l'événement"
            hint="Date à laquelle il a eu lieu, pas celle de la saisie."
          >
            <input
              type="datetime-local"
              value={occurredAt}
              required
              onChange={(event) => setOccurredAt(event.target.value)}
            />
          </Field>

          {type === 'changementProprietaire' && (
            <Field label="Nouveau propriétaire">
              <select
                value={fields.salarieId}
                onChange={(event) => patch({ salarieId: event.target.value })}
              >
                <option value="">Aucun — retour au parc</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {fullName(employee)} — {employee.department}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {type === 'changementEtat' && (
            <Field label="Nouvel état">
              <select
                value={fields.etat}
                onChange={(event) =>
                  patch({ etat: event.target.value as WorkstationState })
                }
              >
                {WORKSTATION_STATES.map((state) => (
                  <option key={state} value={state}>
                    {WORKSTATION_STATE_LABELS[state]}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {type === 'intervention' && (
            <>
              <Field label="Catégorie">
                <select
                  value={fields.categorie}
                  onChange={(event) =>
                    patch({ categorie: event.target.value as InterventionCategory })
                  }
                >
                  {INTERVENTION_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {interventionCategoryLabel(category)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Durée en minutes" hint="Facultatif.">
                <input
                  type="number"
                  min={1}
                  value={fields.dureeMinutes}
                  onChange={(event) => patch({ dureeMinutes: event.target.value })}
                />
              </Field>

              <Field label="Description" wide>
                <textarea
                  value={fields.description}
                  required
                  maxLength={4000}
                  onChange={(event) => patch({ description: event.target.value })}
                />
              </Field>
            </>
          )}

          {type === 'miseAuRebut' && (
            <Field label="Destination" hint="Facultatif : déchèterie, revente, don…">
              <input
                value={fields.destination}
                maxLength={200}
                onChange={(event) => patch({ destination: event.target.value })}
              />
            </Field>
          )}

          <Field
            label="Motif"
            wide
            hint={
              reasonRequired
                ? 'Obligatoire pour ce type : visible sur la frise.'
                : 'Texte libre, visible sur la frise.'
            }
          >
            <textarea
              value={reason}
              required={reasonRequired}
              maxLength={2000}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
